import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { NotesModal, SeverityBadge, EXCEPTION_TYPE_LABELS, fmtDateTime } from './shared';

const API = `${window.__BACKEND_URL__}/api`;

export const ExceptionsTab = ({ token, isAdmin, onChanged }) => {
  const { toast } = useToast();
  const [exceptions, setExceptions] = useState(null);
  const [filters, setFilters] = useState({ type: 'all', severity: 'all', status: 'open' });
  const [resolving, setResolving] = useState(null);
  const [overriding, setOverriding] = useState(null);
  const [rechecking, setRechecking] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filters.status !== 'all' && filters.status !== 'open') params.status = filters.status;
      if (filters.severity !== 'all') params.severity = filters.severity;
      const { data } = await axios.get(`${API}/time/exceptions`,
        { headers: { Authorization: `Bearer ${token}` }, params });
      let list = data.exceptions || [];
      if (filters.status === 'open') list = list.filter((x) => ['open', 'acknowledged'].includes(x.status));
      setExceptions(list);
    } catch (e) { setExceptions([]); }
  }, [token, filters.status, filters.severity]);

  useEffect(() => { load(); }, [load]);

  const refreshAll = () => { load(); onChanged?.(); };

  const resolve = async (notes) => {
    try {
      await axios.put(`${API}/time/exceptions/${resolving.id}/resolve`,
        { resolution_notes: notes }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Exception resolved' });
      setResolving(null);
      refreshAll();
    } catch (e) {
      toast({ title: 'Could not resolve', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const recheck = async (x) => {
    setRechecking(x.id);
    try {
      const { data } = await axios.post(`${API}/time/${x.time_entry_id}/exceptions/recheck`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      const mine = (data.exceptions || []).find((e) => e.id === x.id);
      const cleared = mine && !['open', 'acknowledged'].includes(mine.status);
      toast({
        title: cleared ? 'Exception auto-resolved' : 'Still applies',
        description: cleared
          ? 'The condition no longer applies — the flag was cleared automatically.'
          : 'The times still trigger this exception. Resolve or override it with notes if the work was legitimate.',
      });
      refreshAll();
    } catch (e) {
      toast({ title: 'Re-check failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setRechecking(null);
    }
  };

  const override = async (reason) => {
    try {
      await axios.put(`${API}/time/exceptions/${overriding.id}/override`,
        { override_reason: reason }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Exception overridden', description: 'Overrides are permanent and stay in the audit trail.' });
      setOverriding(null);
      refreshAll();
    } catch (e) {
      toast({ title: 'Could not override', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  if (exceptions === null) {
    return <div className="py-14 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: '#1E88E5' }} /></div>;
  }

  const visible = filters.type === 'all' ? exceptions : exceptions.filter((x) => x.exception_type === filters.type);
  const selStyle = { background: '#1A2433', color: '#fff', border: '1px solid #2D3748' };

  return (
    <div className="space-y-4" data-testid="exceptions-tab">
      <div className="ht-card p-3 flex flex-wrap items-center gap-2" data-testid="exceptions-filter-bar">
        <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          className="px-2 py-1.5 rounded-lg text-xs" style={selStyle} data-testid="exceptions-filter-type">
          <option value="all">All types</option>
          {Object.entries(EXCEPTION_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.severity} onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
          className="px-2 py-1.5 rounded-lg text-xs" style={selStyle} data-testid="exceptions-filter-severity">
          <option value="all">All severities</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="px-2 py-1.5 rounded-lg text-xs" style={selStyle} data-testid="exceptions-filter-status">
          <option value="open">Open / Acknowledged</option>
          <option value="resolved">Resolved</option>
          <option value="overridden">Overridden</option>
          <option value="all">All</option>
        </select>
        <span className="ml-auto text-xs" style={{ color: '#64748B' }} data-testid="exceptions-count">
          {visible.length} exception{visible.length === 1 ? '' : 's'}
        </span>
      </div>

      {visible.length === 0 ? (
        <div className="ht-card p-12 text-center">
          <p className="text-sm" style={{ color: '#64748B' }} data-testid="exceptions-empty">No exceptions match these filters.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((x) => (
            <div key={x.id} className="ht-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`exception-row-${x.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <SeverityBadge severity={x.severity} />
                  <span className="text-sm text-white font-semibold">{EXCEPTION_TYPE_LABELS[x.exception_type] || x.exception_type}</span>
                  <span className="text-xs" style={{ color: '#A0AEC0' }}>· {x.worker_name || x.user_name || ''}</span>
                </div>
                <p className="text-xs mt-1" style={{ color: '#A0AEC0' }}>{x.description}</p>
                <p className="text-[11px] mt-1" style={{ color: '#4A5568' }}>
                  Detected {x.created_at ? fmtDateTime(x.created_at) : ''}
                  {x.status !== 'open' && <> · {x.status} by {x.resolved_by_name || '—'}{x.resolution_notes ? ` — ${x.resolution_notes}` : ''}{x.override_reason ? ` — ${x.override_reason}` : ''}</>}
                </p>
              </div>
              {['open', 'acknowledged'].includes(x.status) && (
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => recheck(x)}
                    disabled={rechecking === x.id}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-60"
                    style={{ background: 'rgba(30,136,229,0.15)', color: '#7BB8E8' }}
                    data-testid={`exception-recheck-btn-${x.id}`}>
                    {rechecking === x.id ? 'Checking…' : 'Re-check'}
                  </button>
                  <button onClick={() => setResolving(x)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg"
                    style={{ background: 'rgba(0,200,150,0.15)', color: '#00C896' }}
                    data-testid={`exception-resolve-btn-${x.id}`}>
                    ✅ Resolve
                  </button>
                  {isAdmin && (
                    <button onClick={() => setOverriding(x)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg"
                      style={{ background: 'rgba(239,68,68,0.15)', color: '#F87171' }}
                      data-testid={`exception-override-btn-${x.id}`}>
                      ⚠️ Override
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {resolving && (
        <NotesModal title="Resolve exception"
          description={`${EXCEPTION_TYPE_LABELS[resolving.exception_type] || resolving.exception_type} — ${resolving.worker_name || ''}`}
          placeholder="How was this verified/handled?"
          submitLabel="Resolve" onSubmit={resolve} onClose={() => setResolving(null)} testId="exceptions-resolve-modal" />
      )}
      {overriding && (
        <NotesModal title="Override exception (permanent)"
          description="Admin only. Overrides are permanent and stay visible in every audit report. Minimum 20 characters."
          minLen={20} placeholder="Why is this exception being permanently overridden?"
          submitLabel="Override" destructive
          onSubmit={override} onClose={() => setOverriding(null)} testId="exceptions-override-modal" />
      )}
    </div>
  );
};

export default ExceptionsTab;
